import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { CarPostAnAdWidget } from "@/components/car-post-ad/CarPostAnAdWidget";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { useEffect, useState } from "react";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { isVehicleInfoComplete } from "@/utils/vehicleInfoCompletion";

const CarPostAnAd = () => {
  const location = useLocation();
  const showPageBack = useShowNavigateBack();
  const { language } = useLanguage();
  const labels = listingPostLabels(language);
  const [initialData, setInitialData] = useState<any>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasVehicleData =
      searchParams.get("regNumber") ||
      searchParams.get("make") ||
      searchParams.get("model") ||
      searchParams.get("year") ||
      searchParams.get("fuelType") ||
      searchParams.get("engine");

    if (hasVehicleData) {
      const vehicleDetails: any = {};

      // Map URL parameters to vehicle details
      if (searchParams.get("make"))
        vehicleDetails.make = searchParams.get("make");
      if (searchParams.get("model"))
        vehicleDetails.model = searchParams.get("model");
      if (searchParams.get("year"))
        vehicleDetails.year = parseInt(searchParams.get("year") || "0");
      if (searchParams.get("fuelType"))
        vehicleDetails.fuelType = searchParams.get("fuelType");
      if (searchParams.get("engine"))
        vehicleDetails.engine = searchParams.get("engine");

      // Vehicle type - prioritize explicit vehicleType, otherwise infer from bodyType
      if (searchParams.get("vehicleType")) {
        vehicleDetails.vehicleType = searchParams.get("vehicleType") as
          | "car"
          | "mc";
      } else {
        // If vehicleType not provided but we have bodyType, assume it's a car (not MC)
        // MCs don't have bodyType, so if bodyType exists, it's definitely a car
        if (searchParams.get("bodyType")) {
          vehicleDetails.vehicleType = "car";
        }
      }
      if (searchParams.get("bodyType"))
        vehicleDetails.bodyType = searchParams.get("bodyType");
      if (searchParams.get("color"))
        vehicleDetails.color = searchParams.get("color");
      if (searchParams.get("transmission"))
        vehicleDetails.transmission = searchParams.get("transmission");
      if (searchParams.get("trim"))
        vehicleDetails.trim = searchParams.get("trim");
      if (searchParams.get("condition"))
        vehicleDetails.condition = searchParams.get("condition");
      if (searchParams.get("mileage")) {
        const mileageValue = searchParams.get("mileage");
        vehicleDetails.mileage =
          typeof mileageValue === "string"
            ? parseInt(mileageValue, 10) || 0
            : mileageValue;
      }
      if (searchParams.get("keys"))
        vehicleDetails.keys = parseInt(searchParams.get("keys") || "0");
      if (searchParams.get("currentTireType"))
        vehicleDetails.currentTireType = searchParams.get("currentTireType");
      if (searchParams.get("currentRimType"))
        vehicleDetails.currentRimType = searchParams.get("currentRimType");
      if (searchParams.get("extraTireSet"))
        vehicleDetails.extraTireSet =
          searchParams.get("extraTireSet") === "true";
      if (searchParams.get("extraTireType"))
        vehicleDetails.extraTireType = searchParams.get("extraTireType");
      if (searchParams.get("extraRimType"))
        vehicleDetails.extraRimType = searchParams.get("extraRimType");
      if (searchParams.get("vin")) vehicleDetails.vin = searchParams.get("vin");
      if (searchParams.get("registrationDate"))
        vehicleDetails.registrationDate =
          searchParams.get("registrationDate") || undefined;

      // Additional fields from valuation flow
      if (searchParams.get("variant"))
        vehicleDetails.variant = searchParams.get("variant");
      if (searchParams.get("horsepower"))
        vehicleDetails.horsepower = searchParams.get("horsepower");
      if (searchParams.get("electricDriveRange"))
        vehicleDetails.electricDriveRange = parseInt(
          searchParams.get("electricDriveRange") || "0",
        );
      if (searchParams.get("engineVolume"))
        vehicleDetails.engineVolume = searchParams.get("engineVolume");
      if (searchParams.get("doors"))
        vehicleDetails.doors = parseInt(searchParams.get("doors") || "0");

      // Handle equipment array (JSON decoded)
      if (searchParams.get("equipment")) {
        try {
          vehicleDetails.equipment = JSON.parse(
            searchParams.get("equipment") || "[]",
          );
        } catch (e) {
          vehicleDetails.equipment = [];
        }
      } else {
        vehicleDetails.equipment = [];
      }

      // Set equipment as empty array if we have vehicle details but no equipment
      if (Object.keys(vehicleDetails).length > 0 && !vehicleDetails.equipment) {
        vehicleDetails.equipment = [];
      }

      // Ensure we have at least basic vehicle info before setting initialData
      const hasBasicInfo =
        vehicleDetails.make || vehicleDetails.model || vehicleDetails.year;

      if (hasBasicInfo) {
        // Valuation already collected Sök + Fordonsinfo — skip straight to Fordonstest
        // when that data is complete; otherwise land on Fordonsinfo to fill gaps.
        const initialStep = isVehicleInfoComplete(vehicleDetails) ? 2 : 1;
        setInitialData({
          regNumber: searchParams.get("regNumber") || "",
          vehicleDetails: vehicleDetails,
          initialStep,
          status: "ongoing", // Default status for new listings
        });
      }
    }
  }, [location.search]);

  return (
    <div className="w-full px-2 sm:px-4 pt-6 md:pt-8">
      <div
        className={
          showPageBack
            ? "grid grid-cols-1 md:grid-cols-[auto_minmax(0,1fr)] items-start gap-y-2 gap-x-3"
            : undefined
        }
      >
        {showPageBack && (
          <BtnNavigateBack
            label={labels.backButton ?? "Tillbaka"}
            to="/list-or-value-car"
            className="!mb-0 w-fit justify-start px-0 md:pt-1"
          />
        )}
        <CarPostAnAdWidget initialData={initialData} isEditMode={false} />
      </div>
    </div>
  );
};

export default CarPostAnAd;
